import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { environment } from 'src/environments/environment'

const DEBUG = environment.logLevel === 'debug'

export interface CookieConsentState {
  hasConsented: boolean // Analytics & Performance + Third-Party Services
  consentDate?: Date
  version: string
  hasDeclined?: boolean
  declineDate?: Date
  advertisingConsent?: boolean // Targeted Advertising
  advertisingConsentDate?: Date
}

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService {
  private readonly CONSENT_KEY = 'h2h_cookie_consent'
  private readonly CONSENT_VERSION = '1.0'

  private consentState = new BehaviorSubject<CookieConsentState>({
    hasConsented: false,
    version: this.CONSENT_VERSION,
  })

  constructor() {
    this.loadConsentState()
    this.setupCrossTabSync()
    // Always load Google AdSense script on init (for non-personalized ads)
    // This ensures ads show immediately, even before user makes a cookie choice
    this.loadGoogleAdSense()
  }

  /**
   * Get the current consent state as an observable
   */
  getConsentState(): Observable<CookieConsentState> {
    return this.consentState.asObservable()
  }

  /**
   * Check if user has consented to cookies
   */
  hasConsented(): boolean {
    return this.consentState.value.hasConsented
  }

  /**
   * Check if user has declined non-necessary cookies
   */
  hasDeclined(): boolean {
    return this.consentState.value.hasDeclined || false
  }

  /**
   * Check if non-necessary cookies can be set (user has explicitly consented)
   */
  canSetNonNecessaryCookies(): boolean {
    return this.hasConsented() || this.canSetAdvertisingCookies()
  }

  /**
   * Check if advertising cookies can be set
   */
  canSetAdvertisingCookies(): boolean {
    return this.consentState.value.advertisingConsent || false
  }

  /**
   * Check if necessary cookies can be set (always true, but useful for clarity)
   */
  canSetNecessaryCookies(): boolean {
    return true // Necessary cookies are always allowed
  }

  /**
   * Grant consent and save to localStorage
   */
  grantConsent(): void {
    const newState: CookieConsentState = {
      hasConsented: true,
      consentDate: new Date(),
      hasDeclined: false, // Clear any previous decline
      advertisingConsent: true, // Accept all cookies includes advertising
      advertisingConsentDate: new Date(),
      version: this.CONSENT_VERSION,
    }

    this.saveConsentState(newState)
    this.consentState.next(newState)

    // CRITICAL: Only load tracking scripts when user explicitly accepts ALL cookies
    // This ensures necessary cookies only until explicit "accept all" consent
    this.loadTrackingScripts()
  }

  /**
   * Grant analytics consent only (without advertising)
   */
  grantAnalyticsConsent(): void {
    const newState: CookieConsentState = {
      ...this.consentState.value,
      hasConsented: true,
      consentDate: new Date(),
      hasDeclined: false, // Clear declined flag when granting any consent
      // Keep existing advertising consent state - don't override it
      version: this.CONSENT_VERSION,
    }

    this.saveConsentState(newState)
    this.consentState.next(newState)

    // Load tracking scripts with current consent state
    this.loadTrackingScripts()
  }

  /**
   * Grant advertising consent separately
   */
  grantAdvertisingConsent(): void {
    const newState: CookieConsentState = {
      ...this.consentState.value,
      advertisingConsent: true,
      advertisingConsentDate: new Date(),
      hasDeclined: false, // Clear declined flag when granting any consent
      version: this.CONSENT_VERSION,
    }

    this.saveConsentState(newState)
    this.consentState.next(newState)

    // Load tracking scripts with current consent state
    this.loadTrackingScripts()
  }

  /**
   * Revoke advertising consent while keeping analytics
   */
  revokeAdvertisingConsent(): void {
    const newState: CookieConsentState = {
      ...this.consentState.value,
      advertisingConsent: false,
      advertisingConsentDate: undefined,
      version: this.CONSENT_VERSION,
    }

    this.saveConsentState(newState)
    this.consentState.next(newState)

    // Update tracking scripts with new consent state
    this.loadTrackingScripts()
  }

  /**
   * Revoke analytics consent while keeping advertising
   */
  revokeAnalyticsConsent(): void {
    const newState: CookieConsentState = {
      ...this.consentState.value,
      hasConsented: false,
      consentDate: undefined,
      hasDeclined: false, // Not a full decline, just analytics revocation
      version: this.CONSENT_VERSION,
    }

    this.saveConsentState(newState)
    this.consentState.next(newState)

    // Update tracking scripts with new consent state
    this.loadTrackingScripts()
  }

  /**
   * User declines non-necessary cookies but allows necessary ones
   */
  declineNonNecessaryCookies(): void {
    const newState: CookieConsentState = {
      hasConsented: false, // Clear any previous consent
      hasDeclined: true,
      declineDate: new Date(),
      advertisingConsent: false, // Reject non-necessary includes rejecting advertising
      version: this.CONSENT_VERSION,
    }

    this.saveConsentState(newState)
    this.consentState.next(newState)

    // CRITICAL: Disable any already-loaded tracking scripts
    this.disableTrackingScripts()
  }

  /**
   * Reset cookie preferences to initial state (no choice made yet)
   * This disables tracking scripts but doesn't set a decline state
   */
  revokeConsent(): void {
    const newState: CookieConsentState = {
      hasConsented: false,
      version: this.CONSENT_VERSION,
    }

    this.saveConsentState(newState)
    this.consentState.next(newState)

    // Disable any already-loaded tracking scripts
    this.disableTrackingScripts()
  }

  /**
   * DEPRECATED: Auto-grant consent removed to respect user cookie preferences
   * Cookie consent is now completely independent of authentication
   * Users must make explicit cookie choices via the cookie popup or settings page
   */
  autoGrantConsentOnAuth(): void {
    // This method is now a no-op to maintain backward compatibility
    // Cookie consent is handled independently of authentication
    console.log(
      'Auto-consent on auth disabled - users must make explicit cookie choices',
    )
  }

  /**
   * Load consent state from localStorage
   */
  private loadConsentState(): void {
    try {
      const stored = localStorage.getItem(this.CONSENT_KEY)
      if (stored) {
        const parsedState: CookieConsentState = JSON.parse(stored)

        // Check if state is still valid (same version)
        if (parsedState.version === this.CONSENT_VERSION) {
          // Always restore the state (whether consented or declined)
          this.consentState.next(parsedState)

          // Load tracking scripts if user has consented to any category
          // The loadTrackingScripts method handles partial consent properly
          if (parsedState.hasConsented || parsedState.advertisingConsent) {
            this.loadTrackingScripts()
          }
        }
      }
    } catch (error) {
      console.warn('Failed to load cookie consent state:', error)
    }
  }

  /**
   * Save consent state to localStorage
   */
  private saveConsentState(state: CookieConsentState): void {
    try {
      localStorage.setItem(this.CONSENT_KEY, JSON.stringify(state))
    } catch (error) {
      console.warn('Failed to save cookie consent state:', error)
    }
  }

  /**
   * Load tracking scripts after consent is granted
   */
  private loadTrackingScripts(): void {
    // If GTM is already loaded, update consent state but continue to check other scripts
    if (this.isGTMLoaded()) {
      this.updateGtmConsent()
      // Continue to check if Stripe script needs to be loaded
    } else {
      // If GTM is NOT loaded, only load if at least one category is consented
      if (!this.hasConsented() && !this.canSetAdvertisingCookies()) {
        // If no consent at all, disable any existing scripts
        this.disableTrackingScripts()
        return
      }

      // Load Google Tag Manager (includes analytics and advertising tags)
      this.loadGTM()

      // Apply current consent state to GTM
      this.updateGtmConsent()
    }

    // Load Stripe pricing table script (necessary for payment functionality)
    // Stripe core payments work without cookies, but pricing tables need the script
    this.loadStripeScript()

    // Load Google AdSense script (always load for non-personalized ads, regardless of consent)
    // Non-personalized ads with data-npa="1" don't require user consent
    this.loadGoogleAdSense()

    // Load other tracking scripts here as needed
    // this.loadOtherTrackingScripts();
  }

  /**
   * Update GTM Consent Mode state based on current preferences
   */
  private updateGtmConsent(): void {
    // Ensure dataLayer exists
    ;(window as any).dataLayer = (window as any).dataLayer || []

    // Helper to push to dataLayer
    function gtag() {
      ;(window as any).dataLayer.push(arguments)
    }

    const analyticsConsent = this.hasConsented() ? 'granted' : 'denied'
    const adConsent = this.canSetAdvertisingCookies() ? 'granted' : 'denied'

    // Update consent state
    // @ts-ignore
    gtag('consent', 'update', {
      analytics_storage: analyticsConsent,
      ad_storage: adConsent,
      ad_user_data: adConsent,
      ad_personalization: adConsent,
    })

    if (DEBUG)
      console.log('GTM Consent Updated:', {
        analytics: analyticsConsent,
        ads: adConsent,
      })
  }

  /**
   * Load advertising-specific tracking scripts
   */
  private loadAdvertisingScripts(): void {
    // For now, advertising tags are loaded with GTM
    // TODO: Implement separate loading or GTM consent signals for proper separation
    if (this.canSetAdvertisingCookies() && !this.isGTMLoaded()) {
      this.loadGTM()
    }
  }

  /**
   * Check if GTM is already loaded
   */
  private isGTMLoaded(): boolean {
    return !!(window as any).google_tag_manager || !!(window as any).gtag
  }

  /**
   * Dynamically load Google Tag Manager
   */
  private loadGTM(): void {
    if (this.isGTMLoaded()) {
      return
    }

    const gtmId = 'GTM-TKLFL8Q'

    // Initialize dataLayer and Consent Mode Defaults
    ;(window as any).dataLayer = (window as any).dataLayer || []
    function gtag() {
      ;(window as any).dataLayer.push(arguments)
    }

    // Default to denied for all categories before GTM loads
    // @ts-ignore
    gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      wait_for_update: 500, // Wait 500ms for update command
    })
    ;(window as any).dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    })

    // Load GTM script
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`

    const firstScript = document.getElementsByTagName('script')[0]
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript)
    }

    // Add noscript iframe for GTM
    const noscript = document.createElement('noscript')
    const iframe = document.createElement('iframe')
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`
    iframe.height = '0'
    iframe.width = '0'
    iframe.style.display = 'none'
    iframe.style.visibility = 'hidden'
    noscript.appendChild(iframe)

    document.body.appendChild(noscript)
  }

  /**
   * Load Stripe pricing table script after consent
   */
  private loadStripeScript(): void {
    // Check if Stripe script is already loaded
    if (document.querySelector('script[src*="js.stripe.com"]')) {
      return
    }

    const script = document.createElement('script')
    script.async = true
    script.src = 'https://js.stripe.com/v3/pricing-table.js'

    const firstScript = document.getElementsByTagName('script')[0]
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript)
    }
  }

  /**
   * Load Google AdSense script after advertising consent is granted.
   * Gated by the `externalAds` env feature flag — if false in the active
   * environment, the script is never injected (H2H028: avoids third-party
   * ad CDNs that can trigger 407 Basic-Auth prompts in Chrome).
   */
  private loadGoogleAdSense(): void {
    if (!environment.featureFlags.externalAds) {
      if (DEBUG)
        console.log(
          'Google AdSense script skipped: externalAds feature flag is off',
        )
      return
    }

    // Check if Google AdSense script is already loaded
    if (
      document.querySelector('script[src*="pagead2.googlesyndication.com"]')
    ) {
      return
    }

    const script = document.createElement('script')
    script.async = true
    script.src =
      'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-7708661664710735'
    script.crossOrigin = 'anonymous'

    const firstScript = document.getElementsByTagName('script')[0]
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript)
    }

    if (DEBUG) console.log('Google AdSense script loaded')
  }

  /**
   * Disable and remove tracking scripts when user declines or revokes consent
   */
  private disableTrackingScripts(): void {
    // Disable Google Tag Manager
    this.disableGTM()

    // Disable Stripe pricing table script
    this.disableStripeScript()

    // Disable Google AdSense script
    this.disableGoogleAdSense()

    // Disable other tracking scripts here as needed
    // this.disableOtherTrackingScripts();
  }

  /**
   * Disable Stripe pricing table script
   */
  private disableStripeScript(): void {
    try {
      // Remove Stripe scripts from DOM
      const stripeScripts = document.querySelectorAll(
        'script[src*="js.stripe.com"]',
      )
      stripeScripts.forEach((script) => {
        script.remove()
      })

      // Clear Stripe global variables if they exist
      delete (window as any).Stripe

      console.log('Stripe script disabled and removed')
    } catch (error) {
      console.warn('Error disabling Stripe script:', error)
    }
  }

  /**
   * Disable Google AdSense script
   * NOTE: We don't actually disable AdSense anymore since non-personalized ads don't require consent
   * This method is kept for backward compatibility but is now a no-op
   */
  private disableGoogleAdSense(): void {
    // Non-personalized ads with data-npa="1" are always safe to show
    // This method is now a no-op to maintain backward compatibility
    console.log('Google AdSense script remains active for non-personalized ads')
  }

  /**
   * Disable Google Tag Manager tracking
   */
  private disableGTM(): void {
    if (!this.isGTMLoaded()) {
      return // GTM not loaded, nothing to disable
    }

    try {
      // Stop GTM data collection
      if ((window as any).gtag) {
        // Disable Google Analytics data collection
        ;(window as any).gtag('consent', 'update', {
          analytics_storage: 'denied',
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
        })
      }

      // Clear dataLayer to stop further tracking
      if ((window as any).dataLayer) {
        ;(window as any).dataLayer.push({
          event: 'consent_revoked',
          consent_analytics: false,
          consent_marketing: false,
        })
      }

      // Remove GTM scripts from DOM
      const gtmScripts = document.querySelectorAll(
        'script[src*="googletagmanager.com"]',
      )
      gtmScripts.forEach((script) => {
        script.remove()
      })

      // Remove GTM noscript iframes
      const gtmIframes = document.querySelectorAll(
        'iframe[src*="googletagmanager.com"]',
      )
      gtmIframes.forEach((iframe) => {
        iframe.parentElement?.remove() // Remove the noscript wrapper too
      })

      // Clear GTM-related cookies
      this.clearGTMCookies()

      // CRITICAL: Clear GTM global variables so it can be reloaded
      // This allows GTM to be re-enabled if user re-consents in same session
      delete (window as any).google_tag_manager
      delete (window as any).gtag
      delete (window as any).dataLayer

      console.log('GTM tracking disabled and scripts removed')
    } catch (error) {
      console.warn('Error disabling GTM:', error)
    }
  }

  /**
   * Clear GTM and Google Analytics cookies
   */
  private clearGTMCookies(): void {
    const gtmCookies = [
      '_ga',
      '_ga_',
      '_gid',
      '_gat',
      '_gtag',
      '_gcl_au',
      '_gcl_aw',
      '_gcl_dc',
      '_gcl_gb',
      '_gcl_gf',
      '_gcl_ha',
    ]

    gtmCookies.forEach((cookieName) => {
      // Clear cookie for current domain
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
      // Clear cookie for parent domain
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`
      // Clear cookie for root domain
      const rootDomain = window.location.hostname.split('.').slice(-2).join('.')
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${rootDomain};`
    })

    // Clear cookies that start with _ga_ (Google Analytics 4)
    const allCookies = document.cookie.split(';')
    allCookies.forEach((cookie) => {
      const cookieName = cookie.split('=')[0].trim()
      if (cookieName.startsWith('_ga_')) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`
        const rootDomain = window.location.hostname
          .split('.')
          .slice(-2)
          .join('.')
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${rootDomain};`
      }
    })
  }

  /**
   * Setup cross-tab synchronization using storage events
   */
  private setupCrossTabSync(): void {
    // Listen for storage changes from other tabs
    window.addEventListener('storage', (event) => {
      if (event.key === this.CONSENT_KEY && event.newValue) {
        try {
          const newState: CookieConsentState = JSON.parse(event.newValue)

          // Only update if version matches and state is different
          if (newState.version === this.CONSENT_VERSION) {
            const currentState = this.consentState.value

            // Check if state actually changed (including advertising consent)
            if (
              currentState.hasConsented !== newState.hasConsented ||
              currentState.hasDeclined !== newState.hasDeclined ||
              currentState.advertisingConsent !== newState.advertisingConsent
            ) {
              // Update local state
              this.consentState.next(newState)

              // Handle tracking scripts based on new state
              // Check if user has declined or has no consent at all
              if (
                newState.hasDeclined ||
                (!newState.hasConsented && !newState.advertisingConsent)
              ) {
                this.disableTrackingScripts()
              } else {
                this.loadTrackingScripts()
              }

              console.log('Cookie consent synchronized across tabs:', newState)
            }
          }
        } catch (error) {
          console.warn('Failed to sync cookie consent across tabs:', error)
        }
      }
    })
  }
}
